import { useState, useRef, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { Country, State, City } from "country-state-city";
import CrossIcon from "../assets/crossIcon.svg";
import Select from "react-select";
import * as Yup from "yup";
import MapBoundaryDrawer from "../addhospital/MapBoundaryDrawer";
import { HospitalServices, updatehospital } from "../services/HospitalServices";
import { handleApiErrorResponses } from "../utils/ApisErrorHandling";
import { toast } from "react-toastify";
import { updateUser } from "../services/AdminServices";
import { AuthServices } from "../services/AuthServices";
const COUNTRIES = ["India"];
const STATES = ["Uttar Pradesh", "Delhi", "Maharashtra", "Karnataka"];
const CITIES = ["Noida", "Ghaziabad", "Lucknow", "Kanpur"];
const DOC_TYPES = [
  "GST Certificate",
  "Trade License",
  "Registration Certificate",
  "PAN Card",
];
const TMS_VERSIONS = ["TMS Version 1", "TMS Version 2", "TMS Version 3"];

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Enter a valid email address")
    .required("Email address is required"),
  mobile: Yup.string()
    .matches(/^[0-9]+$/, "Mobile number must contain only digits")
    .length(10, "Mobile number must be exactly 10 digits")
    .required("Mobile number is required"),
  fullName: Yup.string()
    .trim()
    .matches(/^[A-Za-z\s]+$/, "Full Name must contain only letters and spaces")
    .min(3, "Full Name must be at least 3 characters")
    .required("Full Name is required"),
  designation: Yup.string().required("Please choose a designation"),
  otherDesignation: Yup.string().when("designation", {
    is: "Other",
    then: (schema) => schema.trim().required("Please specify the designation"),
    otherwise: (schema) => schema.notRequired(),
  }),
  hospitalName: Yup.string().trim().required("Hospital name is required"),
  country: Yup.string().required("Country is required"),
  state: Yup.string().required("State is required"),
  city: Yup.string().required("City is required"),
  pincode: Yup.string()
    .matches(/^\d{6}$/, "Pincode must be exactly 6 digits")
    .required("Pincode is required"),
  businessDocType: Yup.string().required(
    "Please select a business document type",
  ),
  businessDocNumber: Yup.string()
    .trim()
    .required("Business document number is required"),
  //   businessDocumentUrl: Yup.mixed().required("Please upload the business document"),
  //   logoUrl: Yup.mixed().nullable(),
  tmsVersion: Yup.string().required("Please select a TMS version"),
});

// Base classes shared by every pill-shaped input/select
const inputBase =
  "w-full rounded-full border boder-solid px-4 py-3 text-sm text-gray-900 outline-none transition-colors focus:border-teal-600";
const inputOk = "border-[#D8D8D8]";
const inputErr = "border-red-500";

function FileTile({
  label,
  file,
  onRemove,
  inputRef,
  onPick,
  error,
  touched,
  accept = "image/*,application/pdf",
}) {
  return (
    <div className="flex flex-col">
      <label className="mb-2 text-sm text-gray-500">{label}</label>
      {!file ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={`flex flex-col items-center gap-1 rounded-lg border-[1.5px] border-dashed bg-gray-50 px-4 py-4 text-sm text-gray-500 ${
            touched && error ? "border-red-500" : "border-gray-300"
          }`}
        >
          <span className="text-xl leading-none text-teal-600">+</span>
          <span>Click to upload</span>
        </button>
      ) : (
        <div className="relative flex items-center gap-2.5 rounded-lg border border-gray-200 bg-white px-3 py-3.5">
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${label}`}
            className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full border border-red-500 bg-white text-sm leading-none text-red-500"
          >
            ×
          </button>
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-gray-100">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none">
              <path
                d="M6 2h9l3 3v17a1 1 0 01-1 1H6a1 1 0 01-1-1V3a1 1 0 011-1z"
                stroke="#9CA3AF"
                strokeWidth="1.4"
              />
              <path
                d="M8 9h8M8 12h8M8 15h5"
                stroke="#9CA3AF"
                strokeWidth="1.2"
              />
            </svg>
          </div>
          <span
            className="min-w-0 truncate text-xs text-gray-700"
            title={file.name}
          >
            {file.name}
          </span>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        hidden
        onChange={onPick}
        accept="image/*,application/pdf"
      />
      {touched && error ? (
        <p className="mt-1.5 text-xs text-red-500">{error}</p>
      ) : null}
    </div>
  );
}

export default function UpdateHospitalDetails({
  setOpen,
  selectedHospital,
  getHospiptalList,
}) {
  console.log("selected hospital:", selectedHospital);
  const [stateList, setStateList] = useState(null);
  const [cityList, setCityList] = useState(null);
  const [selectedState, setSelectedState] = useState(
    selectedHospital?.state ? selectedHospital.state : null,
  );
  const [selectedCity, setSelectedCity] = useState(
    selectedHospital?.city ? selectedHospital.city : null,
  );
  const [hospitalUser, setHospitalUser] = useState(null);
  const docInputRef = useRef(null);
  const logoInputRef = useRef(null);

  const [initialValues, setInitialValues] = useState({
    email: "",
    mobile: "",
    fullName: "",
    designation: "",
    otherDesignation: "",
    hospitalName: selectedHospital?.name || "",
    country: selectedHospital?.country || "",
    state: selectedHospital?.state || "",
    city: selectedHospital?.city || "",
    pincode: selectedHospital?.pincode || "",
    businessDocType: selectedHospital?.businessDocumentType || "",
    businessDocNumber: selectedHospital?.businessDocumentNumber || "",
    businessDocumentUrl: selectedHospital?.businessDocumentUrl || "",
    logoUrl: selectedHospital?.logoUrl || "",
    tmsVersion: selectedHospital?.tmsVersion || "",
    polygon: selectedHospital?.locationDetails?.polygon || [],
  });

  const documentTypeOptions = [
    { value: "PAN", label: "PAN" },
    { value: "HRC", label: "HRC" },
  ];
  const tmsVersionOptions = [
    { value: "1.0", label: "1.0" },
    { value: "2.0", label: "2.0" },
  ];

  const uploadLogoImage = async (e, setFieldValue) => {
    try {
      let data = new FormData();
      data.append("image", e.target.files[0]);
      let res = await AuthServices.uploadImage(data);

      toast.success("Logo image uploaded successfully");

      setFieldValue("logoUrl", res?.data?.url);
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };
  const uploadDocument = async (e, setFieldValue) => {
    try {
      let data = new FormData();
      data.append("image", e.target.files[0]);
      let res = await AuthServices.uploadImage(data);
      toast.success("Document uploaded successfully");
      setFieldValue("businessDocumentUrl", res?.data?.url);
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };
  const handleSubmit = async (values) => {
    const data = {
      ...values,
      name: values.hospitalName,
      locationDetails: {
        polygon: values.polygon,
      },
    };
    const hospitalUserData = {
      userid: hospitalUser?.id,
      fullName: values?.fullName,
      email: values?.email,
      phone: values?.mobile,
      position:
        values?.designation === "Other"
          ? values.otherDesignation
          : values.designation,
    };

    try {
      const res = await updatehospital(selectedHospital?.id, data);
      toast.success(res?.data?.message);
      const userRes = await updateUser(hospitalUserData);
      toast.success(userRes?.data?.message);
      await getHospiptalList();
      setOpen(false);
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };

  // Dropdown chevron as a background-image data URI so plain <select> keeps its native look but matches the pill style
  const selectChevronStyle = {
    appearance: "none",
    backgroundImage:
      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path d='M1 1l5 5 5-5' stroke='%236b7280' stroke-width='1.5' fill='none' fill-rule='evenodd'/></svg>\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 16px center",
    paddingRight: "36px",
  };

  const reactSelectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: 9999, // <-- the pill shape
      borderColor: "#d1d5db",
      padding: "2px 10px",
      minHeight: "auto",
      boxShadow: "none",
      "&:hover": { borderColor: "#d1d5db" },
    }),
    valueContainer: (base) => ({ ...base, padding: "2px 4px" }),
    placeholder: (base) => ({
      ...base,
      fontSize: "0.875rem",
      color: "#9ca3af",
    }),
    singleValue: (base) => ({ ...base, fontSize: "0.875rem" }),
    menu: (base) => ({ ...base, borderRadius: 12, overflow: "hidden" }),
    indicatorSeparator: () => ({ display: "none" }),
  };

  const getHospitalUserByRole = async () => {
    try {
      const res = await HospitalServices.hospitalUserByRole(
        selectedHospital?.id,
        "Hospital",
      );
      res?.data?.Users.length && setHospitalUser(res?.data?.Users?.[0]);
      console.log("hospital user res:", res?.data);
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };

  useEffect(() => {
    getHospitalUserByRole();

    setStateList(State?.getStatesOfCountry("IN"));
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  useEffect(() => {
    setInitialValues((prev) => ({
      ...prev,
      email: hospitalUser?.email,
      mobile: hospitalUser?.phone,
      fullName: hospitalUser?.fullName,
      designation:
        hospitalUser?.position !== "Director" &&
        hospitalUser?.position !== "Promoter"
          ? "Other"
          : hospitalUser?.position,
      otherDesignation:
        hospitalUser?.position !== "Director" &&
        hospitalUser?.position !== "Promoter"
          ? hospitalUser?.position
          : "",
    }));
  }, [hospitalUser]);

  useEffect(() => {
    if (stateList) {
      if (typeof selectedState === "object") {
        setCityList(
          City.getCitiesOfState(
            selectedState?.countryCode,
            selectedState?.isoCode,
          ),
        );
      } else {
        const state = stateList?.filter(
          (state) => state.name === selectedState,
        );
        setCityList(City.getCitiesOfState("IN", state[0]?.isoCode));
      }
    }
  }, [selectedState, stateList]);
  console.log("hospital users:", hospitalUser);

  return (
    <div className="fixed top-0 left-0 h-screen w-screen z-[1300] bg-[#017C5780]">
      <div className="h-[98%] flex flex-col mx-auto my-8 max-w-4xl rounded-[32px] bg-white px-10 py-8 font-sans text-gray-800">
        <div className="flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="mb-4 text-xl font-bold text-gray-900">
              Hospital Details
            </div>
            <div onClick={() => setOpen(false)} className="cursor-pointer">
              <img src={CrossIcon} alt="close-icon" />{" "}
            </div>
          </div>
          <hr className="mb-6 border-gray-200" />
        </div>

        <div className="flex-1 overflow-y-auto">
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              handleSubmit(values);
              setSubmitting(false);
            }}
            validateOnChange={true}
            validateOnBlur={true}
          >
            {({
              values,
              errors,
              touched,
              dirty,
              setFieldValue,
              setFieldError,
              setFieldTouched,
              isSubmitting,
              handleSubmit: formikHandleSubmit,
            }) => {
              const cls = (name) =>
                `${inputBase} ${touched[name] && errors[name] ? inputErr : inputOk}`;

              return (
                <Form
                  noValidate
                  className="space-y-5"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!dirty) {
                      toast.info("Do some changes before saving.");
                      return;
                    }
                    formikHandleSubmit(e);
                  }}
                >
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div>
                      <label
                        htmlFor="email"
                        className="mb-2 block text-sm text-gray-500"
                      >
                        Email Address
                      </label>
                      <Field
                        id="email"
                        name="email"
                        type="text"
                        placeholder="Johndoe@gmail.com"
                        className={cls("email")}
                      />
                      <ErrorMessage
                        name="email"
                        component="p"
                        className="mt-1.5 text-xs text-red-500"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="mobile"
                        className="mb-2 block text-sm text-gray-500"
                      >
                        Mobile Number
                      </label>
                      <Field
                        id="mobile"
                        name="mobile"
                        type="text"
                        placeholder="9012345678"
                        maxLength={10}
                        onInput={(e) => {
                          e.target.value = e.target.value.replace(
                            /[^0-9]/g,
                            "",
                          );
                          setFieldValue("mobile", e.target.value);
                        }}
                        className={cls("mobile")}
                      />
                      <ErrorMessage
                        name="mobile"
                        component="p"
                        className="mt-1.5 text-xs text-red-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="fullName"
                      className="mb-2 block text-sm text-gray-500"
                    >
                      Enter your Full Name
                    </label>
                    <Field
                      id="fullName"
                      name="fullName"
                      type="text"
                      placeholder="Dr. John Doe"
                      className={cls("fullName")}
                    />
                    <ErrorMessage
                      name="fullName"
                      component="p"
                      className="mt-1.5 text-xs text-red-500"
                    />
                  </div>

                  <div>
                    <span className="mb-2 block text-sm text-gray-500">
                      Choose Designation
                    </span>
                    <div className="flex flex-wrap items-center gap-7">
                      {["Director", "Promoter", "Other"].map((option) => (
                        <label
                          key={option}
                          className="flex cursor-pointer items-center gap-2 text-sm text-gray-700"
                        >
                          <Field
                            type="radio"
                            name="designation"
                            value={option}
                            className="h-4 w-4 accent-teal-600"
                          />
                          {option}
                        </label>
                      ))}
                      <Field
                        type="text"
                        name="otherDesignation"
                        placeholder="Type Here"
                        disabled={values.designation !== "Other"}
                        className={`${cls("otherDesignation")} min-w-[180px] flex-1 disabled:bg-gray-50 disabled:text-gray-400`}
                      />
                    </div>
                    <ErrorMessage
                      name="designation"
                      component="p"
                      className="mt-1.5 text-xs text-red-500"
                    />
                    {values.designation === "Other" && (
                      <ErrorMessage
                        name="otherDesignation"
                        component="p"
                        className="mt-1.5 text-xs text-red-500"
                      />
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="hospitalName"
                      className="mb-2 block text-sm text-gray-500"
                    >
                      Hospital Name
                    </label>
                    <Field
                      id="hospitalName"
                      name="hospitalName"
                      type="text"
                      placeholder="City Care Hospital"
                      className={cls("hospitalName")}
                    />
                    <ErrorMessage
                      name="hospitalName"
                      component="p"
                      className="mt-1.5 text-xs text-red-500"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-4">
                    <div>
                      <label
                        htmlFor="country"
                        className="mb-2 block text-sm text-gray-500"
                      >
                        Country
                      </label>
                      <Field
                        as="select"
                        id="country"
                        name="country"
                        style={selectChevronStyle}
                        className={cls("country")}
                      >
                        <option value="">Select</option>
                        {COUNTRIES.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="country"
                        component="p"
                        className="mt-1.5 text-xs text-red-500"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="state"
                        className="mb-2 block text-sm text-gray-500"
                      >
                        State
                      </label>
                      <Select
                        name="state"
                        options={stateList}
                        getOptionLabel={(option) => option["name"]}
                        getOptionValue={(option) => option["name"]}
                        value={stateList?.filter(
                          (state) => state.name === values.state,
                        )}
                        onChange={(item) => {
                          setSelectedState(item);
                          setFieldValue("state", item.name);
                        }}
                        onBlur={() => setFieldTouched("state", true)}
                        placeholder="Select State"
                        styles={reactSelectStyles}
                      />
                      <ErrorMessage
                        name="state"
                        component="p"
                        className="mt-1.5 text-xs text-red-500"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="city"
                        className="mb-2 block text-sm text-gray-500"
                      >
                        City
                      </label>
                      <Select
                        name="city"
                        options={cityList}
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.name}
                        value={
                          cityList?.find((city) => city.name === values.city) ||
                          null
                        }
                        onChange={(item) => {
                          setSelectedCity(item);
                          setFieldValue("city", item ? item.name : "");
                        }}
                        onBlur={() => setFieldTouched("city", true)}
                        placeholder="Select City"
                        styles={reactSelectStyles}
                      />
                      <ErrorMessage
                        name="city"
                        component="p"
                        className="mt-1.5 text-xs text-red-500"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="pincode"
                        className="mb-2 block text-sm text-gray-500"
                      >
                        Pincode
                      </label>
                      <Field
                        id="pincode"
                        name="pincode"
                        type="text"
                        maxLength={6}
                        placeholder="123456"
                        className={cls("pincode")}
                      />
                      <ErrorMessage
                        name="pincode"
                        component="p"
                        className="mt-1.5 text-xs text-red-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div>
                      <label
                        htmlFor="businessDocType"
                        className="mb-2 block text-sm text-gray-500"
                      >
                        Business Document Type
                      </label>
                      <Field
                        as="select"
                        id="businessDocType"
                        name="businessDocType"
                        style={selectChevronStyle}
                        className={cls("businessDocType")}
                      >
                        <option value="">Select Business Document Type</option>
                        {documentTypeOptions.map((d) => (
                          <option key={d.value} value={d.value}>
                            {d.label}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="businessDocType"
                        component="p"
                        className="mt-1.5 text-xs text-red-500"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="businessDocNumber"
                        className="mb-2 block text-sm text-gray-500"
                      >
                        Business Document Number
                      </label>
                      <Field
                        id="businessDocNumber"
                        name="businessDocNumber"
                        type="text"
                        placeholder="1234567890"
                        className={cls("businessDocNumber")}
                      />
                      <ErrorMessage
                        name="businessDocNumber"
                        component="p"
                        className="mt-1.5 text-xs text-red-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3">
                    <FileTile
                      label="Business Document"
                      file={values.businessDocumentUrl}
                      inputRef={docInputRef}
                      touched={touched.businessDocumentUrl}
                      error={errors.businessDocumentUrl}
                      accept="image/jpeg,image/jpg,image/png,application/pdf"
                      onPick={(e) => {
                        const file = e.target.files[0];
                        setFieldTouched("businessDocumentUrl", true);

                        if (file) {
                          const allowedTypes = [
                            "image/jpeg",
                            "image/jpg",
                            "image/png",
                            "application/pdf",
                          ];
                          const allowedExtensions = /\.(jpe?g|png|pdf)$/i;

                          const isValidType =
                            allowedTypes.includes(file.type) &&
                            allowedExtensions.test(file.name);

                          if (!isValidType) {
                            toast.error(
                              "Only JPG, JPEG, and PNG files are allowed",
                            );
                            if (docInputRef.current)
                              docInputRef.current.value = "";
                            return; // block upload — AVI, TIFF, or anything else stops here
                          }
                        }

                        uploadDocument(e, setFieldValue);
                      }}
                      onRemove={() => {
                        setFieldValue("businessDocumentUrl", null);
                        if (docInputRef.current) docInputRef.current.value = "";
                      }}
                    />

                    <div>
                      {" "}
                      <FileTile
                        label="Hospital LOGO"
                        file={values.logoUrl}
                        inputRef={logoInputRef}
                        touched={touched.logoUrl}
                        error={errors.logoUrl}
                        accept="image/jpeg,image/jpg,image/png"
                        onPick={(e) => {
                          const file = e.target.files[0];
                          setFieldTouched("logoUrl", true);

                          if (file) {
                            const allowedTypes = [
                              "image/jpeg",
                              "image/jpg",
                              "image/png",
                            ];
                            const allowedExtensions = /\.(jpe?g|png)$/i;
                            const isValidType =
                              allowedTypes.includes(file.type) &&
                              allowedExtensions.test(file.name);

                            if (!isValidType) {
                              toast.error(
                                "Only JPG, JPEG, and PNG files are allowed",
                              );
                              if (logoInputRef.current)
                                logoInputRef.current.value = "";
                              return;
                            }
                          }

                          uploadLogoImage(e, setFieldValue);
                        }}
                        onRemove={() => {
                          setFieldValue("logoUrl", null);
                          if (logoInputRef.current)
                            logoInputRef.current.value = "";
                        }}
                      />
                      <ErrorMessage
                        name="logoUrl"
                        component="p"
                        className="mt-1.5 text-xs text-red-500"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="tmsVersion"
                        className="mb-2 block text-sm text-gray-500"
                      >
                        TMS Version
                      </label>
                      <Field
                        as="select"
                        id="tmsVersion"
                        name="tmsVersion"
                        style={selectChevronStyle}
                        className={cls("tmsVersion")}
                      >
                        <option value="">Select</option>
                        {tmsVersionOptions.map((v) => (
                          <option key={v.value} value={v.value}>
                            {v.label}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="tmsVersion"
                        component="p"
                        className="mt-1.5 text-xs text-red-500"
                      />
                    </div>
                  </div>

                  <div>
                    <MapBoundaryDrawer
                      initialBoundary={values.polygon}
                      onBoundaryChange={(coords) => {
                        console.log("coords:", coords);
                        setFieldValue("polygon", coords);
                      }}
                    />{" "}
                  </div>

                  <div className="flex justify-center border-t border-gray-200 pt-5">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="rounded-full bg-teal-700 px-12 py-3 text-sm font-semibold text-white transition-colors hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Save
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </div>
  );
}
